import { useEffect, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { FONT_OPTIONS } from "@constants/font-options";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUITheme, AXNUIThemeGeneral } from "@iddataweb/idw-common-ui/types";
import { SelectOption } from "@idw-types/common";
import { loadFont } from "@iddataweb/idw-common-ui/utils";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingLayoutStyleSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiTheme: AXNUITheme;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    updateAXNUIThemeGeneralFieldValue: (key: keyof AXNUIThemeGeneral, value: string) => void;
};

export const ThemingLayoutStyleSection = (props: ThemingLayoutStyleSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiTheme,
        inheritedAXNUITheme,
        pageLevel,
        updateAXNUIThemeGeneralFieldValue
    } = props;

    useEffect(() => {
        if (axnuiTheme.general.font) {
            loadFont(axnuiTheme.general.font, "axnui-font");
        }
    }, [axnuiTheme.general.font]);

    const handleFontChange = (newValue: SelectOption) => {
        updateAXNUIThemeGeneralFieldValue("font", newValue.label);
    };

    const handleResetFont = () => {
        updateAXNUIThemeGeneralFieldValue("font", inheritedAXNUITheme.general.font);
    };

    const handleFontColorChange = (newValue: string) => {
        updateAXNUIThemeGeneralFieldValue("mainTextColor", newValue);
    };

    const handleResetFontColor = () => {
        updateAXNUIThemeGeneralFieldValue("mainTextColor", inheritedAXNUITheme.general.mainTextColor);
    };

    const handleLinksColorChange = (newValue: string) => {
        updateAXNUIThemeGeneralFieldValue("linksColor", newValue);
    };

    const handleResetLinksColor = () => {
        updateAXNUIThemeGeneralFieldValue("linksColor", inheritedAXNUITheme.general.linksColor);
    };

    const handleMainButtonTextColorChange = (newValue: string) => {
        updateAXNUIThemeGeneralFieldValue("mainButtonTextColor", newValue);
    };

    const handleResetMainButtonTextColor = () => {
        updateAXNUIThemeGeneralFieldValue("mainButtonTextColor", inheritedAXNUITheme.general.mainButtonTextColor);
    };

    const handleMainButtonBackgroundColorChange = (newValue: string) => {
        updateAXNUIThemeGeneralFieldValue("mainButtonBackgroundColor", newValue);
    };

    const handleResetMainButtonBackgroundColor = () => {
        updateAXNUIThemeGeneralFieldValue("mainButtonBackgroundColor", inheritedAXNUITheme.general.mainButtonBackgroundColor);
    };

    const selectedFontOption = useMemo(() => {
        if (axnuiTheme.general.font) {
            return FONT_OPTIONS.find(fo => fo.label === axnuiTheme.general.font);
        }
        return null;
    }, [axnuiTheme.general.font]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.font}
                currentThemingPropertyValue={axnuiTheme.general.font}
                pageLevel={pageLevel}
            >
                <SelectFormGroup
                    controlId={"theming-layout-font-select-input"}
                    automationId={"theming-layout-font-select-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.font.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.font.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleFontChange}
                    options={FONT_OPTIONS}
                    selectedOption={selectedFontOption}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.font.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFont}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.mainTextColor}
                currentThemingPropertyValue={axnuiTheme.general.mainTextColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-layout-font-color-picker"}
                    automationId={"theming-layout-font-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.fontColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.fontColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.general.mainTextColor || ""}
                    onChange={handleFontColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.fontColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFontColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.linksColor}
                currentThemingPropertyValue={axnuiTheme.general.linksColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-layout-links-color-picker"}
                    automationId={"theming-layout-links-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.linksColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.linksColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.general.linksColor || ""}
                    onChange={handleLinksColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.linksColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetLinksColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.mainButtonTextColor}
                currentThemingPropertyValue={axnuiTheme.general.mainButtonTextColor}
                pageLevel={pageLevel}
            >

                <ColorPickerFormGroup
                    controlId={"theming-layout-main-button-text-color-picker"}
                    automationId={"theming-layout-main-button-text-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.mainButtonTextColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.mainButtonTextColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.general.mainButtonTextColor || ""}
                    onChange={handleMainButtonTextColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.mainButtonTextColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetMainButtonTextColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.mainButtonBackgroundColor}
                currentThemingPropertyValue={axnuiTheme.general.mainButtonBackgroundColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-layout-main-button-background-color-picker"}
                    automationId={"theming-layout-main-button-background-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.mainButtonBackgroundColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.mainButtonBackgroundColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.general.mainButtonBackgroundColor || ""}
                    onChange={handleMainButtonBackgroundColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.mainButtonBackgroundColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetMainButtonBackgroundColor}
                />
            </ThemingFormGroupWrapper>
        </div>
    );
};